import type { UsageIndicatorProps } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getMySpendOptions } from "@/api";
import { useUsageIndicatorLabels } from "@/components/designLabels";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { formatCost, TREND_MONTHS, utcMonthStart } from "@/features/settings/usage/spend";
import { formatCompact, formatDate, formatNumber } from "@/i18n/format";

const MONTH_DAY = { month: "long", day: "numeric", timeZone: "UTC" } as const;
const PENDING = "–";

/**
 * The sidebar usage ring: the caller's monthly budget plus this month's AI usage. Undefined (ring hidden) when no
 * budget applies, as frontend/'s BudgetBanner. Spend shares the Usage page's query, so both read one cache entry.
 */
export function useSidebarUsage(): UsageIndicatorProps | undefined {
  const { t } = useTranslation("shell", { keyPrefix: "usage" });
  const navigate = useNavigate();
  const labels = useUsageIndicatorLabels();
  const budget = useCurrentUser().data?.budget;
  const spend = useQuery({
    ...getMySpendOptions({ query: { months: TREND_MONTHS } }),
    enabled: Boolean(budget),
    select: (r) => r.data,
  });
  if (!budget) return undefined;
  const s = spend.data;
  return {
    labels,
    budget: {
      spent: formatCost(budget.currentSpendUsd),
      limit: formatCost(budget.monthlyLimitUsd),
      percentUsed: budget.percentUsed,
      resetsLabel: t("resets", { date: formatDate(budget.resetsAt, MONTH_DAY) }),
    },
    stats: {
      periodLabel: formatDate(utcMonthStart(), MONTH_DAY),
      cost: s ? formatCost(s.currentMonthCostUsd) : PENDING,
      requests: s ? formatNumber(s.currentMonthRequests) : PENDING,
      tokensIn: s ? formatCompact(s.currentMonthTokensInput) : PENDING,
      tokensOut: s ? formatCompact(s.currentMonthTokensOutput) : PENDING,
    },
    onViewAllUsage: () => navigate("/settings/usage"),
  };
}
